import React from 'react';
import {
  Box,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { GlassButton } from 'dashboard/components/common';

/**
 * Extra pieces in the header bar, beyond the mark and the menu.
 *
 * The footer got columns for a logo, a sentence and grouped links; the header
 * got neither, so a business whose header needed "Call 555 0123" or a "Book
 * now" button had nowhere to put it.
 *
 * Kinds rather than free-form HTML, because each one wants different
 * behaviour: a phone number should be tappable on a mobile, an email should
 * open a mail client, a button should look like a button. A single "custom
 * content" box would be simpler to build and would produce four different
 * broken versions of a phone link.
 */

const KINDS = [
  { value: 'button', label: 'Button', hint: 'A path on your site, like /contact' },
  { value: 'phone', label: 'Phone number', hint: 'Tappable on a phone' },
  { value: 'email', label: 'Email address', hint: 'Opens their mail app' },
  { value: 'text', label: 'Short text', hint: 'A strapline or opening hours' },
];

export default function HeaderExtras({ extras = [], onChange }) {
  const set = (i, patch) => onChange(extras.map((x, j) => (i === j ? { ...x, ...patch } : x)));

  const valueLabel = (kind) => {
    if (kind === 'button') return 'Goes to';
    if (kind === 'phone') return 'Number to dial';
    if (kind === 'email') return 'Email address';
    return '';
  };

  return (
    <Box>
      <Typography variant='caption' color='text.secondary' display='block' mb='10px'>
        A phone number, a strapline or a call-to-action button, shown at the end of the bar.
      </Typography>

      <Stack spacing='10px'>
        {extras.map((x, i) => {
          const kind = x.kind || 'text';
          const needsValue = kind !== 'text';
          return (
            <Stack
              key={i}
              direction={{ xs: 'column', sm: 'row' }}
              spacing='8px'
              alignItems={{ sm: 'flex-start' }}
            >
              <FormControl size='small' sx={{ minWidth: 140 }}>
                <InputLabel>Kind</InputLabel>
                <Select
                  label='Kind'
                  value={kind}
                  onChange={(e) => set(i, { kind: e.target.value })}
                >
                  {KINDS.map((k) => <MenuItem key={k.value} value={k.value}>{k.label}</MenuItem>)}
                </Select>
              </FormControl>

              <TextField
                size='small'
                label='What it says'
                value={x.label || ''}
                onChange={(e) => set(i, { label: e.target.value })}
                placeholder={kind === 'phone' ? 'Call 555 0123' : 'Book now'}
                sx={{ flex: 1 }}
              />

              {needsValue && (
                <TextField
                  size='small'
                  label={valueLabel(kind)}
                  value={x.value || ''}
                  onChange={(e) => set(i, { value: e.target.value })}
                  placeholder={kind === 'button' ? '/contact' : kind === 'phone' ? '+420555000123' : 'hello@acme.com'}
                  helperText={KINDS.find((k) => k.value === kind)?.hint}
                  sx={{ flex: 1 }}
                />
              )}

              <Tooltip title='Remove'>
                <IconButton
                  size='small'
                  color='error'
                  onClick={() => onChange(extras.filter((_, j) => j !== i))}
                  sx={{ mt: { sm: '4px' } }}
                >
                  <DeleteIcon fontSize='small' />
                </IconButton>
              </Tooltip>
            </Stack>
          );
        })}
      </Stack>

      <GlassButton
        size='small'
        startIcon={<AddIcon />}
        onClick={() => onChange([...extras, { kind: 'button', label: '', value: '/contact' }])}
        disabled={extras.length >= 3}
        disabledReason={extras.length >= 3
          ? 'Three is as much as a header bar holds before the menu starts wrapping.'
          : ''}
        sx={{ mt: '10px' }}
      >
        Add to header
      </GlassButton>
    </Box>
  );
}
